/**
 * SlModeChip — shows WHEN this trade's stop counts as hit, and lets the user give one
 * trade a different rule than their default (PUT /trades/:id/sl-mode).
 *
 *   "2m hold"      Price + hold (the default rule)
 *   "4H close"     Candle close on 4H bars, emergency stop shown in the tooltip
 *   a dot (•)      this trade overrides the Settings → Stop-Loss Plan default
 *
 * Row data comes from the Dashboard summary's `slModeInfo` (backend resolveSlMode),
 * so the chip always shows the rule the engine actually applies.
 */
import React, { useState } from 'react';
import { Chip, Menu, MenuItem, ListItemText, Divider, Tooltip, ListSubheader } from '@mui/material';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAppTheme } from '../../hooks/useAppTheme';
import fmt from '../../utils/priceFormat';

export interface SlModeInfo {
  mode: 'HOLD' | 'CANDLE_CLOSE';
  tfMinutes: number;
  source: 'trade' | 'default';
  emergencyR: number;
  emergencyPrice: number | null;
  holdMinutes: number;
  backupStopEnabled: boolean;
}

export const tfShort = (m: number) =>
  m % 1440 === 0 ? `${m / 1440}D` : m % 60 === 0 ? `${m / 60}H` : `${m}m`;

const TF = [5, 15, 30, 60, 120, 240, 1440];

/** One-line description for tooltips. */
export function slModeLine(i: SlModeInfo | undefined | null, instr?: any): string {
  if (!i) return '';
  const who = i.source === 'trade' ? 'this trade' : 'your default';
  if (i.mode === 'CANDLE_CLOSE') {
    const em = i.emergencyPrice != null ? ` @ ${fmt.price(i.emergencyPrice, instr)}` : '';
    return `Stop counts on a ${tfShort(i.tfMinutes)} candle CLOSE beyond it (${who}); emergency stop ${i.emergencyR}R further${em} closes at once after 10 s`;
  }
  return `Stop counts after price stays beyond it for ${i.holdMinutes}m (${who})`;
}

const SlModeChip: React.FC<{
  tradeId: number;
  info?: SlModeInfo | null;
  /** The trade's own timeframe (originIntervalMinutes), for the menu label. */
  ownTf?: number | null;
  instrument?: any;
  backup?: { price?: number | null; orderId?: string | null; error?: string | null };
  disabled?: boolean;
  onChanged?: () => void;
}> = ({ tradeId, info, ownTf, instrument, backup, disabled, onChanged }) => {
  const { C } = useAppTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);
  if (!info) return null;
  const candle = info.mode === 'CANDLE_CLOSE';
  const label = `${candle ? `${tfShort(info.tfMinutes)} close` : `${info.holdMinutes}m hold`}${info.source === 'trade' ? ' •' : ''}`;
  const backupLine = info.backupStopEnabled
    ? backup?.error ? `Backup stop at Delta FAILED: ${backup.error}`
      : backup?.orderId && backup?.price != null ? `Backup stop at Delta @ ${fmt.price(backup.price, instrument)}` : 'Backup stop at Delta: not placed yet'
    : '';
  const tip = [slModeLine(info, instrument), backupLine, disabled ? '' : 'Click to change for this trade only'].filter(Boolean).join(' | ');

  const apply = async (mode: 'HOLD' | 'CANDLE_CLOSE' | null, tfMinutes: number | null = null) => {
    setAnchor(null); setBusy(true);
    try {
      await api.put(`/trades/${tradeId}/sl-mode`, { mode, tfMinutes });
      toast.success(mode == null ? 'Stop rule: back to your default' : mode === 'HOLD' ? 'Stop rule: price + hold' : `Stop rule: ${tfMinutes ? tfShort(tfMinutes) : "trade's own TF"} candle close`);
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Could not change the stop rule');
    }
    setBusy(false);
  };

  return (
    <>
      <Tooltip title={tip} arrow>
        <Chip size="small" label={label} data-testid="sl-mode-chip" disabled={busy}
          onClick={disabled ? undefined : (e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
            bgcolor: candle ? C.neutralMuted : 'transparent', color: candle ? C.neutral : 'text.secondary',
            border: `1px solid ${info.backupStopEnabled && backup?.error ? C.bear : candle ? C.neutralBorder : C.dimMuted}` }} />
      </Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} onClick={e => e.stopPropagation()}>
        <ListSubheader sx={{ lineHeight: '28px' }}>When does this trade's stop count?</ListSubheader>
        <MenuItem dense selected={info.source === 'default'} onClick={() => apply(null)}>
          <ListItemText primary="Use my default" secondary="Settings → Stop-Loss Plan" />
        </MenuItem>
        <Divider />
        <MenuItem dense selected={info.source === 'trade' && !candle} onClick={() => apply('HOLD')}>
          <ListItemText primary={`Price + hold (${info.holdMinutes}m)`} />
        </MenuItem>
        <MenuItem dense onClick={() => apply('CANDLE_CLOSE', null)}>
          <ListItemText primary={`Candle close — trade's own TF${ownTf ? ` (${tfShort(ownTf)})` : ''}`} />
        </MenuItem>
        {TF.map(m => (
          <MenuItem dense key={m} selected={info.source === 'trade' && candle && info.tfMinutes === m} onClick={() => apply('CANDLE_CLOSE', m)}>
            <ListItemText primary={`Candle close — ${tfShort(m)}`} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem dense onClick={() => { setAnchor(null); window.open('/settings?tab=sl-plan', '_blank', 'noopener'); }}>
          <ListItemText primary="Open the Stop-Loss Plan…" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default SlModeChip;
